import { InfoTip } from "@/components/ui/info-tip";
import { useEffect, useState } from "react";
import { PhoneCall, RotateCcw, Save } from "lucide-react";
import { ApiError } from "@/lib/api";
import { clearTeamOnCall, saveTeamOnCall } from "@/lib/tickets";
import { ErrorNote } from "./shared";

/**
 * The number an urgent request should ring outside this team's working hours.
 *
 * ⚠️ Beside the hours panel, deliberately — "when does the clock advance" and "who do you call
 * when it doesn't" are the same question from two sides, and separating them would put half the
 * answer on another screen (the same reasoning `TeamHolidaysPanel` gives for sitting there too).
 *
 * ⚠️ Optional, and most teams have none. The new-request wizard's after-hours banner simply
 * omits the "call" line when this is unset — never a broken or empty number.
 */
export function TeamOnCallPanel({
  teamId,
  onCallPhone,
  onChanged,
}: {
  teamId: string;
  onCallPhone: string | null;
  onChanged: () => Promise<void>;
}) {
  const [phone, setPhone] = useState(onCallPhone ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPhone(onCallPhone ?? "");
  }, [onCallPhone]);

  async function run(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="group/card rounded-lg border border-border">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <PhoneCall className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <h3 className="flex min-w-0 flex-1 items-center gap-1 text-sm font-medium">
          On-call number
          <InfoTip id="team-oncall" />
        </h3>

        {onCallPhone ? (
          <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
            set
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">not set</span>
        )}
      </header>

      <div className="space-y-3 p-3">
        <p className="text-xs text-muted-foreground">
          Shown to a requester when this team is outside its working hours, for something that
          cannot wait until the next business day. Leave it blank if this team has none.
        </p>

        {error && <ErrorNote message={error} />}

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="tel"
            aria-label="On-call phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(561) 555-0142"
            className="h-10 w-full max-w-xs rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
          />

          {onCallPhone && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(() => clearTeamOnCall(teamId), "That number could not be removed.")
              }
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-3 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Clear
            </button>
          )}

          <button
            type="button"
            disabled={busy || phone.trim().length === 0 || phone === (onCallPhone ?? "")}
            onClick={() =>
              void run(() => saveTeamOnCall(teamId, phone), "That number could not be saved.")
            }
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" aria-hidden />
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </section>
  );
}
